import { useRef, useEffect, useState, useContext } from 'react';
import videojs, { VideoJSPlayer } from 'video.js';
import classNames from 'classnames';
import noop from 'lodash/noop';

import { videoJsOptions } from '../../../media-player/video-player.constants';
import { VolumeSlider } from '../volume-slider/volume-slider';
import { Pause } from '../../../../general/icon/components/pause';
import { Play } from '../../../../general/icon/components/play';

import 'video.js/dist/video-js.css';
import styles from './audio-player.module.scss';
import { handleCommonMediaShortcuts } from '../../../pop-out-media-manager/pop-out-media-manager.helpers';
import { PopOutAudioContext } from '../../pop-out-audio';
import type { PopOutAudioContextValue, PlaylistAudioItem } from '../../types';
import { handleAudioSeekShortcuts } from './audio-player.helpers';

type AudioPlayerProps = {
  title: string | null | undefined;
  type: string | null | undefined;
  url: string | null | undefined;
  playing: boolean;
  onAudioEnded: () => void;
  onAudioPlayed: () => void;
  onAudioPaused: () => void;
  onVolumeClicked: () => void;
  onPlayerReady: (player?: VideoJSPlayer | null | undefined) => void;
  onError: () => void;
  onRequestClose?: () => void;
};

export const AudioPlayer = ({
  url,
  type,
  title,
  onAudioEnded = noop,
  onAudioPaused = noop,
  onAudioPlayed = noop,
  playing = false,
  onVolumeClicked = noop,
  onPlayerReady = noop,
  onError = noop,
  onRequestClose = noop,
}: AudioPlayerProps) => {
  const playerRef = useRef<HTMLAudioElement | null>(null);
  const player = useRef<VideoJSPlayer | null>(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [volume, setVolume] = useState(0);
  const {
    getCurrentlyActive,
    playAudio,
    playNextAudio,
    playPreviousAudio,
    modalDialogClosedLatch,
    setModalDialogClosedLatch,
  } = useContext<PopOutAudioContextValue>(PopOutAudioContext);

  useEffect(() => {
    if (!playerRef.current) {
      return;
    }
    player.current = videojs(playerRef.current, videoJsOptions(true), () => {
      setPlayerReady(true);
      if (player.current) {
        setVolume(player.current.volume());
      }
      onPlayerReady(player.current);
    });
  }, [playerRef.current]);

  useEffect(() => {
    const onVolumeChanged = () => {
      if (!player.current) {
        return;
      }

      setVolume(player.current.volume());
    };

    const handleOnPause = () => {
      onAudioPaused();

      // Some browsers pause audio when a modal dialog closes.
      // The latch indicates that playback should be restarted after the pause
      const currentlyActive: PlaylistAudioItem | null = getCurrentlyActive();

      if (currentlyActive && modalDialogClosedLatch) {
        playAudio(currentlyActive?.itemId);
        setModalDialogClosedLatch(false);
      }
    };

    /**
     * The callback passed as onAudioEnded may change in reference,
     * so each time we bind it we need to make sure that we unbind
     * the old reference when it changes.
     */
    if (playerReady && player.current) {
      player.current.on('ended', onAudioEnded);
      (player.current as VideoJSPlayer).on('play', onAudioPlayed);
      (player.current as VideoJSPlayer).on('pause', handleOnPause);
      (player.current as VideoJSPlayer).on('error', onError);
      (player.current as VideoJSPlayer).on('volumechange', onVolumeChanged);
    }

    return () => {
      if (player.current) {
        player.current.off('ended', onAudioEnded);
        (player.current as VideoJSPlayer).off('play', onAudioPlayed);
        (player.current as VideoJSPlayer).off('error', onError);
        (player.current as VideoJSPlayer).off('pause', handleOnPause);
        (player.current as VideoJSPlayer).off('volumechange', onVolumeChanged);
      }
    };
  }, [playerReady, onAudioEnded, onAudioPaused, onAudioPlayed, player.current]);

  useEffect(() => {
    const keyDownHandler = (evt: KeyboardEvent) => {
      if (!url) {
        return;
      }

      const didPerformCommonAction = handleCommonMediaShortcuts(
        evt,
        player.current,
        onRequestClose,
      );

      if (didPerformCommonAction) {
        return;
      }

      handleAudioSeekShortcuts(evt, playNextAudio, playPreviousAudio);
    };

    document.addEventListener('keydown', keyDownHandler);

    return () => document.removeEventListener('keydown', keyDownHandler);
  }, [player.current, url, playPreviousAudio, playNextAudio]);

  useEffect(() => {
    if (!player.current || !url) {
      return;
    }
    player.current.src({
      type: type || '',
      src: url,
    });
    // flow thinks the previous function call may have nulled the value
    // so we cast the type forcibly
    (player.current as VideoJSPlayer).currentTime(0);
    (player.current as VideoJSPlayer).trigger('loadstart');
    (player.current as VideoJSPlayer).play();
  }, [url, player.current]);

  useEffect(() => {
    if (!player.current) {
      return;
    }
    if (playing) {
      player.current.play();
    } else {
      player.current.pause();
    }
  }, [playing]);

  const togglePlayState = () => {
    if (!player.current) {
      return;
    }
    if (playing) {
      player.current.pause();
    } else {
      player.current.play();
    }
  };

  return (
    <div className={styles['audio-player']}>
      <div className={styles['audio-player__content']}>
        <button
          data-hook='audio-player-play-toggle'
          onMouseDown={e => e.preventDefault()}
          className={styles['audio-player__play-state-container']}
          onClick={togglePlayState}
          aria-label={polyglot.t(`audio_player.playing.${playing.toString()}`)}
        >
          <div className={styles['audio-player__play-state-icon']}>
            {playing ? <Pause /> : <Play />}
          </div>
        </button>
        <div className={styles['audio-player__player-container']}>
          <div className={styles['audio-player__title']}>{title}</div>
          <div className={styles['audio-player__scrubber-container']}>
            <audio
              controls
              ref={playerRef}
              className={classNames(
                'vjs-default-skin',
                'video-js',
                'vjs-audio',
                styles['audio-player-element'],
              )}
              preload='auto'
            />
          </div>
        </div>
        <div className={styles['audio-player__buttons']}>
          <VolumeSlider
            onVolumeClicked={onVolumeClicked}
            volume={volume}
            onVolumeChanged={vol => {
              if (player.current) {
                player.current.volume(vol);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
